// Procedural Jungle habitat: sky, noise hills, hanging bezier vines, grass, fireflies, weather.
import { Container, Graphics, GraphicsContext } from "pixi.js";
import { Rng } from "../lib/rng";
import { INK, OKABE_ITO, mix } from "../palette";
import { AmbienceFollower, clamp01, nightness, smoothK } from "./common";
import { ParticlePool, verticalGradient } from "./draw";
import { createNoise1D, fbm1D, type Noise1D } from "./noise";
import type { AmbienceInput, Habitat, HabitatOptions, HabitatVariant } from "./types";

interface SkyPalette {
  top: number;
  horizon: number;
  hill: number;
  ground: number;
}

const SKY: Record<HabitatVariant["timeOfDay"], SkyPalette> = {
  dawn: { top: 0x4a5a9a, horizon: 0xf5b88a, hill: 0x3f8f73, ground: 0x4f8a3c },
  day: { top: 0x3d9fd6, horizon: 0xcdeef9, hill: 0x2e9a6b, ground: 0x5a9e3a },
  dusk: { top: 0x2b2a5e, horizon: 0xe8915a, hill: 0x2f6d5a, ground: 0x3f6e33 },
  night: { top: 0x070b20, horizon: 0x1d2a4a, hill: 0x173a38, ground: 0x1f3d2a },
};

interface Swayer {
  node: Container;
  phase: number;
  speed: number;
  amount: number;
  /** 0 = rotation sway, 1 = skew sway */
  mode: 0 | 1;
}

const LEAF_GREENS = [0x009e73, 0x2bb88f, 0x00865f, 0x6fd3b4, 0x4caf50];

export function createJungle(opts: HabitatOptions): Habitat {
  let w = Math.max(1, opts.width);
  let h = Math.max(1, opts.height);
  let variant = { ...opts.variant };
  let reduced = opts.reducedMotion;
  let particles = opts.particles;
  const seed = opts.seed >>> 0;

  const hillNoise = [createNoise1D(seed + 1), createNoise1D(seed + 2), createNoise1D(seed + 3)];
  const groundNoise = createNoise1D(seed + 4);

  const view = new Container();
  view.label = "habitat:jungle";

  const sky = new Graphics();
  const celestialLayer = new Container();
  const celestial = new Graphics();
  celestialLayer.addChild(celestial);
  const stars = [new Graphics(), new Graphics()];
  const starLayer = new Container();
  starLayer.addChild(...stars);
  const hillLayers = [new Container(), new Container(), new Container()];
  const hills = hillLayers.map((c) => c.addChild(new Graphics()));
  const ground = new Graphics();
  const grassLayer = new Container();
  const vineLayer = new Container();
  const canopy = new Graphics();
  const weatherTint = new Graphics();
  const mistLayer = new Container();
  const light = new Graphics();
  light.blendMode = "add";

  const fireflyCtx = new GraphicsContext()
    .circle(0, 0, 7)
    .fill({ color: OKABE_ITO.yellow, alpha: 0.18 })
    .circle(0, 0, 3.5)
    .fill({ color: OKABE_ITO.yellow, alpha: 0.45 })
    .circle(0, 0, 1.8)
    .fill({ color: 0xfffbe0, alpha: 1 });
  const fireflies = new ParticlePool(fireflyCtx, 90);
  fireflies.container.blendMode = "add";

  const rainCtx = new GraphicsContext()
    .moveTo(0, 0)
    .lineTo(-3, 14)
    .stroke({ width: 2, color: 0xa6daf6, alpha: 0.65, cap: "round" });
  const rain = new ParticlePool(rainCtx, 160);
  const snowCtx = new GraphicsContext().circle(0, 0, 2.6).fill({ color: 0xffffff, alpha: 0.9 });
  const snow = new ParticlePool(snowCtx, 120);

  view.addChild(
    sky,
    starLayer,
    celestialLayer,
    ...hillLayers,
    ground,
    grassLayer,
    vineLayer,
    fireflies.container,
    weatherTint,
    mistLayer,
    rain.container,
    snow.container,
    light,
  );
  vineLayer.addChild(canopy);

  const swayers: Swayer[] = [];
  const mists: { g: Graphics; speed: number }[] = [];
  const amb = new AmbienceFollower();
  let time = 0;
  const parallax = { tx: 0, ty: 0, x: 0, y: 0 };
  const margin = () => Math.round(w * 0.06);

  function groundYAt(xf: number): number {
    return h * 0.8 + fbm1D(groundNoise, xf * 2.2 + 3.1, 3) * h * 0.035;
  }

  function hillPath(
    g: Graphics,
    noise: Noise1D,
    base: number,
    amp: number,
    freq: number,
    color: number,
    outline: number,
  ): void {
    const m = margin();
    const pts: number[] = [];
    const step = Math.max(6, w / 160);
    for (let x = -m; x <= w + m + step; x += step) {
      const y = base + fbm1D(noise, (x / w) * freq + 10, 4) * amp;
      pts.push(x, y);
    }
    const top = pts.slice();
    pts.push(w + m + step, h + 4, -m, h + 4);
    g.poly(pts, true).fill(color);
    if (outline > 0)
      g.poly(top, false).stroke({ width: outline, color: INK, alpha: 0.35, join: "round", cap: "round" });
  }

  function leafPoints(cx: number, cy: number, len: number, wid: number, angle: number): number[] {
    // Leaf drawn as a polygon from two quadratic-ish arcs.
    const pts: number[] = [];
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    const n = 8;
    for (let side = 0; side < 2; side++) {
      for (let i = 0; i <= n; i++) {
        const t = side === 0 ? i / n : 1 - i / n;
        const lx = t * len;
        const ly = Math.sin(t * Math.PI) * wid * (side === 0 ? 1 : -1) * (1 - t * 0.3);
        pts.push(cx + lx * ca - ly * sa, cy + lx * sa + ly * ca);
      }
    }
    return pts;
  }

  function build(): void {
    const rng = new Rng(seed ^ 0x9e3779b9);
    const pal = SKY[variant.timeOfDay];
    const night = nightness(variant.timeOfDay);
    const m = margin();

    // Sky
    sky.clear();
    verticalGradient(sky, -m, -m, w + 2 * m, h * 0.8 + m, [
      [0, pal.top],
      [0.75, pal.horizon],
      [1, mix(pal.horizon, pal.hill, 0.25)],
    ]);
    sky.rect(-m, h * 0.8 - 1, w + 2 * m, h * 0.2 + m + 1).fill(mix(pal.horizon, pal.hill, 0.25));

    // Stars (two groups so they can twinkle out of phase)
    for (const s of stars) s.clear();
    const starAlpha =
      variant.timeOfDay === "night" ? 1 : variant.timeOfDay === "dusk" ? 0.35 : variant.timeOfDay === "dawn" ? 0.15 : 0;
    starLayer.visible = starAlpha > 0;
    starLayer.alpha = starAlpha;
    if (starAlpha > 0) {
      for (let i = 0; i < 90; i++) {
        const sx = rng.range(-m, w + m);
        const sy = rng.range(0, h * 0.55);
        const r = rng.range(0.6, 1.8);
        stars[i % 2].circle(sx, sy, r).fill({ color: 0xfffbe0, alpha: rng.range(0.5, 1) });
      }
    } else {
      // keep RNG sequence stable regardless of variant
      for (let i = 0; i < 270; i++) rng.float();
    }

    // Sun / moon
    celestial.clear();
    const R = Math.min(w, h) * 0.07;
    let cx = w * 0.78;
    let cy = h * 0.2;
    let color: number = OKABE_ITO.yellow;
    if (variant.timeOfDay === "dawn") {
      cx = w * 0.18;
      cy = h * 0.58;
      color = 0xf7c96b;
    } else if (variant.timeOfDay === "dusk") {
      cx = w * 0.82;
      cy = h * 0.6;
      color = OKABE_ITO.orange;
    } else if (variant.timeOfDay === "night") {
      color = 0xf5f1d0;
      cy = h * 0.17;
    }
    for (let i = 4; i >= 1; i--) celestial.circle(cx, cy, R * (1 + i * 0.45)).fill({ color, alpha: 0.06 });
    celestial.circle(cx, cy, R).fill(color).stroke({ width: 4, color: INK, alpha: 0.5 });
    if (variant.timeOfDay === "night") {
      celestial.circle(cx - R * 0.3, cy - R * 0.2, R * 0.18).fill({ color: 0xd9d3a8 });
      celestial.circle(cx + R * 0.35, cy + R * 0.25, R * 0.12).fill({ color: 0xd9d3a8 });
      celestial.circle(cx + R * 0.1, cy - R * 0.45, R * 0.08).fill({ color: 0xd9d3a8 });
    }

    // Hills, far → near, fading into the horizon (atmospheric perspective)
    const hillCfg = [
      { base: 0.56, amp: 0.09, freq: 2.2, haze: 0.55, outline: 0 },
      { base: 0.64, amp: 0.07, freq: 3.4, haze: 0.32, outline: 3 },
      { base: 0.72, amp: 0.05, freq: 5.0, haze: 0.12, outline: 4 },
    ];
    hillCfg.forEach((c, i) => {
      const g = hills[i];
      g.clear();
      const col = mix(mix(pal.hill, OKABE_ITO.bluishGreen, i * 0.15), pal.horizon, c.haze);
      hillPath(g, hillNoise[i], h * c.base, h * c.amp, c.freq, col, c.outline);
      // lollipop trees on the mid hills
      if (i >= 1) {
        const trees = i === 1 ? 14 : 8;
        for (let k = 0; k < trees; k++) {
          const tx = rng.range(-m, w + m);
          const ty = h * c.base + fbm1D(hillNoise[i], (tx / w) * c.freq + 10, 4) * h * c.amp + 3;
          const s = rng.range(0.6, 1.2) * h * (i === 1 ? 0.03 : 0.045);
          const tc = mix(col, INK, 0.18 + rng.range(0, 0.1));
          g.rect(tx - s * 0.12, ty - s * 1.4, s * 0.24, s * 1.4).fill(mix(tc, 0x6b4a2b, 0.4));
          g.circle(tx, ty - s * 1.6, s * 0.75)
            .fill(tc)
            .stroke({ width: 2, color: INK, alpha: 0.3 });
        }
      }
    });

    // Ground (fixed so creatures can stand on it)
    ground.clear();
    const gcol = pal.ground;
    const gpts: number[] = [];
    const step = Math.max(6, w / 160);
    for (let x = -m; x <= w + m + step; x += step) gpts.push(x, groundYAt(x / w));
    const gtop = gpts.slice();
    gpts.push(w + m + step, h + m, -m, h + m);
    ground.poly(gpts, true).fill(gcol);
    // darker soil band and specks
    for (let i = 0; i < 70; i++) {
      const x = rng.range(-m, w + m);
      const y = groundYAt(x / w) + rng.range(10, h * 0.2);
      ground.circle(x, y, rng.range(1.5, 4)).fill({ color: mix(gcol, INK, 0.3), alpha: 0.5 });
    }
    // tiny flowers on the ground line
    const flowerCols = [OKABE_ITO.reddishPurple, OKABE_ITO.yellow, OKABE_ITO.skyBlue, OKABE_ITO.orange];
    for (let i = 0; i < 18; i++) {
      const x = rng.range(0, w);
      const y = groundYAt(x / w) + rng.range(6, 24);
      const fc = mix(rng.pick(flowerCols), INK, night * 0.5);
      for (let p = 0; p < 5; p++) {
        const a = (p / 5) * Math.PI * 2;
        ground.circle(x + Math.cos(a) * 3, y + Math.sin(a) * 3, 2.4).fill(fc);
      }
      ground.circle(x, y, 1.8).fill(0xfff3a8);
    }
    ground.poly(gtop, false).stroke({ width: 4, color: INK, alpha: 0.55, join: "round", cap: "round" });

    // Swaying things: grass tufts, foreground leaves, vines
    for (const s of swayers) if (s.node !== canopy) s.node.destroy({ children: true });
    swayers.length = 0;
    grassLayer.removeChildren();
    vineLayer.removeChildren();
    vineLayer.addChild(canopy);

    const grassCol = mix(gcol, OKABE_ITO.bluishGreen, 0.35);
    const tufts = Math.round(w / 45);
    for (let i = 0; i < tufts; i++) {
      const x = rng.range(0, w);
      const tuft = new Graphics();
      const blades = rng.int(3, 5);
      const th = rng.range(10, 22) * (h / 700);
      for (let b = 0; b < blades; b++) {
        const bx = (b - (blades - 1) / 2) * 4;
        const lean = rng.range(-6, 6);
        tuft
          .moveTo(bx - 2.5, 0)
          .quadraticCurveTo(bx + lean * 0.4, -th * 0.6, bx + lean, -th * rng.range(0.7, 1.1))
          .quadraticCurveTo(bx + lean * 0.4 + 1, -th * 0.5, bx + 2.5, 0)
          .closePath()
          .fill(mix(grassCol, INK, rng.range(0, 0.2)))
          .stroke({ width: 1.5, color: INK, alpha: 0.5 });
      }
      tuft.position.set(x, groundYAt(x / w) + 2);
      grassLayer.addChild(tuft);
      swayers.push({ node: tuft, phase: rng.range(0, 6.28), speed: rng.range(1.2, 2), amount: 0.25, mode: 1 });
    }
    // big foreground leaves rising from the bottom corners
    const fgLeaves = 6;
    for (let i = 0; i < fgLeaves; i++) {
      const left = i % 2 === 0;
      const x = left ? rng.range(-w * 0.02, w * 0.12) : rng.range(w * 0.88, w * 1.02);
      const leaf = new Graphics();
      const len = h * rng.range(0.12, 0.2);
      const ang = -Math.PI / 2 + (left ? 1 : -1) * rng.range(0.15, 0.6);
      leaf
        .poly(leafPoints(0, 0, len, len * 0.22, ang), true)
        .fill(mix(rng.pick(LEAF_GREENS), INK, 0.15 + night * 0.45))
        .stroke({ width: 3, color: INK, alpha: 0.7 });
      const ca = Math.cos(ang);
      const sa = Math.sin(ang);
      leaf
        .moveTo(0, 0)
        .lineTo(ca * len * 0.9, sa * len * 0.9)
        .stroke({ width: 2, color: INK, alpha: 0.35 });
      leaf.position.set(x, h + 6);
      grassLayer.addChild(leaf);
      swayers.push({ node: leaf, phase: rng.range(0, 6.28), speed: rng.range(0.6, 1.1), amount: 0.05, mode: 0 });
    }

    // Canopy band along the top
    canopy.clear();
    const canopyCol = mix(0x00865f, INK, 0.2 + night * 0.45);
    for (let x = -m; x < w + m; x += rng.range(28, 46)) {
      const r = rng.range(26, 48) * (h / 700 + 0.3);
      canopy
        .circle(x, rng.range(-r * 0.6, -r * 0.1), r)
        .fill(mix(canopyCol, rng.pick(LEAF_GREENS), rng.range(0, 0.35)))
        .stroke({ width: 3, color: INK, alpha: 0.55 });
    }

    // Vines: cubic beziers hanging from the top with leaves along the curve
    const vines = 5 + rng.int(0, 3);
    for (let i = 0; i < vines; i++) {
      const vx = ((i + rng.range(0.2, 0.8)) / vines) * w;
      const len = h * rng.range(0.14, 0.38);
      const dx = rng.range(-30, 30);
      const c1x = rng.range(-40, 40);
      const c2x = rng.range(-40, 40);
      const g = new Graphics();
      const p0 = [0, 0];
      const p1 = [c1x, len * 0.33];
      const p2 = [c2x, len * 0.66];
      const p3 = [dx, len];
      const vineCol = mix(0x3a6b2a, INK, night * 0.5);
      g.moveTo(0, 0)
        .bezierCurveTo(p1[0], p1[1], p2[0], p2[1], p3[0], p3[1])
        .stroke({ width: 7, color: INK, alpha: 0.8, cap: "round" });
      g.moveTo(0, 0)
        .bezierCurveTo(p1[0], p1[1], p2[0], p2[1], p3[0], p3[1])
        .stroke({ width: 4, color: vineCol, cap: "round" });
      const leaves = Math.round(len / 22);
      for (let k = 1; k <= leaves; k++) {
        const t = k / (leaves + 0.5);
        const u = 1 - t;
        const bx = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
        const by = u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1];
        const side = k % 2 === 0 ? 1 : -1;
        const ang = Math.PI / 2 + side * rng.range(0.7, 1.2);
        const ll = rng.range(12, 20);
        g.poly(leafPoints(bx, by, ll, ll * 0.32, ang), true)
          .fill(mix(rng.pick(LEAF_GREENS), INK, night * 0.45))
          .stroke({ width: 2, color: INK, alpha: 0.7 });
      }
      g.position.set(vx, 0);
      vineLayer.addChild(g);
      swayers.push({ node: g, phase: rng.range(0, 6.28), speed: rng.range(0.5, 0.9), amount: 0.06, mode: 0 });
    }

    // Weather tint + mist
    weatherTint.clear();
    const tintAlpha =
      variant.weather === "rain" ? 0.18 : variant.weather === "snow" ? 0.08 : variant.weather === "mist" ? 0.1 : 0;
    if (tintAlpha > 0)
      weatherTint
        .rect(-m, -m, w + 2 * m, h + 2 * m)
        .fill({ color: variant.weather === "rain" ? 0x2a3a50 : 0xdfe8f0, alpha: tintAlpha });
    for (const ms of mists) ms.g.destroy();
    mists.length = 0;
    mistLayer.removeChildren();
    if (variant.weather === "mist") {
      for (let i = 0; i < 7; i++) {
        const g = new Graphics();
        const rw = w * rng.range(0.25, 0.45);
        const rh = h * rng.range(0.05, 0.1);
        for (let k = 0; k < 4; k++)
          g.ellipse(
            rng.range(-rw * 0.3, rw * 0.3),
            rng.range(-rh * 0.3, rh * 0.3),
            rw * rng.range(0.5, 1),
            rh * rng.range(0.6, 1),
          ).fill({ color: 0xf2f5f7, alpha: 0.07 });
        g.position.set(rng.range(0, w), rng.range(h * 0.45, h * 0.85));
        mistLayer.addChild(g);
        mists.push({ g, speed: rng.range(6, 16) * (rng.chance(0.5) ? 1 : -1) });
      }
    }

    light.clear();
    light.rect(-m, -m, w + 2 * m, h + 2 * m).fill({ color: 0xfff1c4, alpha: 1 });
    light.alpha = 0;

    fireflies.clear();
    rain.clear();
    snow.clear();
    applyStatic();
  }

  /** Reset transforms for reduced motion. */
  function applyStatic(): void {
    for (const s of swayers) {
      if (s.mode === 0) s.node.rotation = 0;
      else s.node.skew.x = 0;
    }
  }

  function fireflyTarget(): number {
    if (!particles) return 0;
    const base =
      variant.timeOfDay === "night" ? 30 : variant.timeOfDay === "dusk" ? 22 : variant.timeOfDay === "dawn" ? 10 : 5;
    const wet = variant.weather === "rain" ? 0.4 : 1;
    return Math.min(fireflies.capacity, Math.round((base + amb.level * 55) * wet));
  }

  function updateFireflies(dt: number): void {
    const target = fireflyTarget();
    let active = fireflies.activeCount;
    // spawn a few per frame toward the target
    let spawnBudget = 3;
    while (active < target && spawnBudget-- > 0) {
      const p = fireflies.spawn();
      if (!p) break;
      const xf = Math.random();
      p.x = xf * w;
      p.y = groundYAt(xf) - Math.random() * h * 0.45;
      p.vx = (Math.random() - 0.5) * 20;
      p.vy = (Math.random() - 0.5) * 10;
      p.life = 4 + Math.random() * 6;
      p.phase = Math.random() * 6.28;
      p.size = 0.7 + Math.random() * 0.6;
      active++;
    }
    const night = nightness(variant.timeOfDay);
    const bright = 0.35 + 0.35 * night + 0.5 * amb.level + 0.6 * amb.pulse;
    for (const p of fireflies.items) {
      if (!p.active) continue;
      p.age += dt;
      // retire extras gracefully by shortening life
      if (active > target && p.age < p.life - 1) {
        p.age = p.life - 1;
        active--;
      }
      if (p.age >= p.life) {
        fireflies.kill(p);
        continue;
      }
      const wander = time * 0.8 + p.phase;
      p.vx += Math.sin(wander * 1.3) * 18 * dt;
      p.vy += Math.cos(wander * 0.9) * 12 * dt;
      p.vx *= 0.98;
      p.vy *= 0.98;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.x < -10) p.x = w + 10;
      if (p.x > w + 10) p.x = -10;
      const fadeIn = clamp01(p.age / 0.8);
      const fadeOut = clamp01((p.life - p.age) / 1);
      const blink = 0.55 + 0.45 * Math.sin(time * 3 + p.phase * 3);
      p.g.position.set(p.x, p.y);
      p.g.alpha = Math.min(1, fadeIn * fadeOut * blink * bright);
      p.g.scale.set(p.size * (1 + amb.pulse * 0.6));
    }
  }

  function updateWeather(dt: number): void {
    const doRain = particles && variant.weather === "rain";
    const doSnow = particles && variant.weather === "snow";
    if (!doRain) rain.clear();
    if (!doSnow) snow.clear();
    if (doRain) {
      for (let k = 0; k < 6; k++) {
        const p = rain.spawn();
        if (!p) break;
        p.x = Math.random() * (w + 60);
        p.y = -20 - Math.random() * h * 0.3;
        p.vx = -90;
        p.vy = 650 + Math.random() * 200;
        p.size = 0.7 + Math.random() * 0.6;
      }
      for (const p of rain.items) {
        if (!p.active) continue;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.y > groundYAt(clamp01(p.x / w)) + 10) rain.kill(p);
        else {
          p.g.position.set(p.x, p.y);
          p.g.scale.set(p.size);
        }
      }
    }
    if (doSnow) {
      if (Math.random() < 0.8) {
        const p = snow.spawn();
        if (p) {
          p.x = Math.random() * w;
          p.y = -10;
          p.vy = 25 + Math.random() * 30;
          p.phase = Math.random() * 6.28;
          p.size = 0.5 + Math.random() * 0.8;
          p.life = 30;
        }
      }
      for (const p of snow.items) {
        if (!p.active) continue;
        p.y += p.vy * dt;
        p.x += Math.sin(time * 0.9 + p.phase) * 18 * dt;
        if (p.y > h + 10) snow.kill(p);
        else {
          p.g.position.set(p.x, p.y);
          p.g.scale.set(p.size);
        }
      }
    }
  }

  build();

  const habitat: Habitat = {
    view,
    resize(nw, nh) {
      w = Math.max(1, nw);
      h = Math.max(1, nh);
      build();
    },
    setVariant(v) {
      if (v.timeOfDay === variant.timeOfDay && v.weather === variant.weather) return;
      variant = { ...v };
      build();
    },
    setMotion(r, p) {
      reduced = r;
      particles = p;
      if (!particles) {
        fireflies.clear();
        rain.clear();
        snow.clear();
      }
      if (reduced) applyStatic();
    },
    update(dt, a: AmbienceInput) {
      dt = Math.min(0.1, Math.max(0, dt));
      time += dt;
      amb.update(dt, a);

      // Parallax
      const k = smoothK(dt, 0.25);
      parallax.x += ((reduced ? 0 : parallax.tx) - parallax.x) * k;
      parallax.y += ((reduced ? 0 : parallax.ty) - parallax.y) * k;
      const m = margin();
      celestialLayer.position.set(-parallax.x * m * 0.1, -parallax.y * m * 0.05);
      starLayer.position.set(-parallax.x * m * 0.05, 0);
      hillLayers.forEach((c, i) => c.position.set(-parallax.x * m * (0.2 + i * 0.2), -parallax.y * m * 0.05 * (i + 1)));
      vineLayer.position.set(-parallax.x * m * 0.9, 0);

      if (!reduced) {
        const sway = 0.35 + amb.level * 1.4 + amb.pulse * 0.6;
        for (const s of swayers) {
          const v = Math.sin(time * s.speed + s.phase) * s.amount * sway;
          if (s.mode === 0) s.node.rotation = v;
          else s.node.skew.x = v;
        }
        stars[0].alpha = 0.7 + 0.3 * Math.sin(time * 1.7);
        stars[1].alpha = 0.7 + 0.3 * Math.sin(time * 1.3 + 2);
        for (const ms of mists) {
          ms.g.x += ms.speed * dt;
          if (ms.g.x > w * 1.4) ms.g.x = -w * 0.4;
          if (ms.g.x < -w * 0.4) ms.g.x = w * 1.4;
        }
        light.alpha = amb.pulse * 0.07 + amb.level * 0.03;
      } else {
        light.alpha = amb.level * 0.03;
      }
      if (particles) {
        updateFireflies(dt);
        updateWeather(dt);
      }
      // subtle celestial glow with the music
      celestial.alpha = 0.9 + 0.1 * amb.level;
    },
    setParallax(x, y) {
      parallax.tx = Math.max(-1, Math.min(1, x));
      parallax.ty = Math.max(-1, Math.min(1, y));
    },
    groundY(xf) {
      return groundYAt(xf);
    },
    destroy() {
      fireflies.destroy();
      rain.destroy();
      snow.destroy();
      view.destroy({ children: true });
    },
  };
  return habitat;
}
